import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Trash2, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPanel({ user }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('brands');
  
  // Data states
  const [brands, setBrands] = useState([]);
  const [channels, setChannels] = useState([]);
  const [subchannels, setSubchannels] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [metricMappings, setMetricMappings] = useState([]);
  
  // Form states
  const [brandName, setBrandName] = useState('');
  const [channelData, setChannelData] = useState({ brand_id: '', name: '' });
  const [subchannelData, setSubchannelData] = useState({ channel_id: '', name: '' });
  const [campaignData, setCampaignData] = useState({ subchannel_id: '', name: '', funnel_stage: '' });
  const [mappingData, setMappingData] = useState({ source_name: '', standard_metric: '', source_type: 'google_sheets' });
  
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is admin
    if (user && user.role !== 'admin') {
      toast.error('Access denied. Admin privileges required.');
      navigate('/dashboard');
      return;
    }
    fetchAllData();
  }, [user, navigate]);

  const fetchAllData = async () => {
    try {
      const [brandsRes, channelsRes, subchannelsRes, campaignsRes, mappingsRes] = await Promise.all([
        fetch(`${API}/brands`, { credentials: 'include' }),
        fetch(`${API}/channels`, { credentials: 'include' }),
        fetch(`${API}/subchannels`, { credentials: 'include' }),
        fetch(`${API}/campaigns`, { credentials: 'include' }),
        fetch(`${API}/metric-mappings`, { credentials: 'include' })
      ]);

      setBrands(await brandsRes.json());
      setChannels(await channelsRes.json());
      setSubchannels(await subchannelsRes.json());
      setCampaigns(await campaignsRes.json());
      setMetricMappings(await mappingsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Brands
  const createBrand = async (e) => {
    e.preventDefault();
    if (!brandName.trim()) return;
    
    try {
      const res = await fetch(`${API}/brands`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: brandName })
      });
      if (!res.ok) throw new Error('Failed to create brand');
      const newBrand = await res.json();
      setBrands([...brands, newBrand]);
      setBrandName('');
      toast.success('Brand created successfully');
    } catch (error) {
      toast.error('Failed to create brand');
    }
  };

  const deleteBrand = async (id) => {
    if (!confirm('Are you sure? This will delete all related data.')) return;
    
    try {
      const res = await fetch(`${API}/brands/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to delete');
      setBrands(brands.filter(b => b.brand_id !== id));
      toast.success('Brand deleted');
    } catch (error) {
      toast.error('Failed to delete brand');
    }
  };

  // Channels
  const createChannel = async (e) => {
    e.preventDefault();
    if (!channelData.brand_id || !channelData.name.trim()) return;
    
    try {
      const res = await fetch(`${API}/channels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(channelData)
      });
      if (!res.ok) throw new Error('Failed to create channel');
      const newChannel = await res.json();
      setChannels([...channels, newChannel]);
      setChannelData({ brand_id: '', name: '' });
      toast.success('Channel created successfully');
    } catch (error) {
      toast.error('Failed to create channel');
    }
  };

  const deleteChannel = async (id) => {
    if (!confirm('Are you sure? This will delete all related data.')) return;
    
    try {
      const res = await fetch(`${API}/channels/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to delete');
      setChannels(channels.filter(c => c.channel_id !== id));
      toast.success('Channel deleted');
    } catch (error) {
      toast.error('Failed to delete channel');
    }
  };

  // SubChannels
  const createSubchannel = async (e) => {
    e.preventDefault();
    if (!subchannelData.channel_id || !subchannelData.name.trim()) return;
    
    try {
      const res = await fetch(`${API}/subchannels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(subchannelData)
      });
      if (!res.ok) throw new Error('Failed to create subchannel');
      const newSubchannel = await res.json();
      setSubchannels([...subchannels, newSubchannel]);
      setSubchannelData({ channel_id: '', name: '' });
      toast.success('Sub-channel created successfully');
    } catch (error) {
      toast.error('Failed to create sub-channel');
    }
  };

  const deleteSubchannel = async (id) => {
    if (!confirm('Are you sure? This will delete all related data.')) return;
    
    try {
      const res = await fetch(`${API}/subchannels/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to delete');
      setSubchannels(subchannels.filter(s => s.subchannel_id !== id));
      toast.success('Sub-channel deleted');
    } catch (error) {
      toast.error('Failed to delete sub-channel');
    }
  };

  // Campaigns
  const createCampaign = async (e) => {
    e.preventDefault();
    if (!campaignData.subchannel_id || !campaignData.name.trim()) return;
    
    try {
      const res = await fetch(`${API}/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          subchannel_id: campaignData.subchannel_id,
          name: campaignData.name,
          funnel_stage: campaignData.funnel_stage || null
        })
      });
      if (!res.ok) throw new Error('Failed to create campaign');
      const newCampaign = await res.json();
      setCampaigns([...campaigns, newCampaign]);
      setCampaignData({ subchannel_id: '', name: '', funnel_stage: '' });
      toast.success('Campaign created successfully');
    } catch (error) {
      toast.error('Failed to create campaign');
    }
  };

  const deleteCampaign = async (id) => {
    if (!confirm('Are you sure? This will delete all related data.')) return;
    
    try {
      const res = await fetch(`${API}/campaigns/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to delete');
      setCampaigns(campaigns.filter(c => c.campaign_id !== id));
      toast.success('Campaign deleted');
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  // Metric Mappings
  const createMapping = async (e) => {
    e.preventDefault();
    if (!mappingData.source_name.trim() || !mappingData.standard_metric) return;
    
    try {
      const res = await fetch(`${API}/metric-mappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(mappingData)
      });
      if (!res.ok) throw new Error('Failed to create mapping');
      const newMapping = await res.json();
      setMetricMappings([...metricMappings, newMapping]);
      setMappingData({ source_name: '', standard_metric: '', source_type: 'google_sheets' });
      toast.success('Metric mapping created successfully');
    } catch (error) {
      toast.error('Failed to create metric mapping');
    }
  };

  const deleteMapping = async (id) => {
    try {
      const res = await fetch(`${API}/metric-mappings/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to delete');
      setMetricMappings(metricMappings.filter(m => m.mapping_id !== id));
      toast.success('Mapping deleted');
    } catch (error) {
      toast.error('Failed to delete mapping');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <Sidebar user={user} currentPage="admin" />
      
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 md:p-8 max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-[#0F0F0F] tracking-tight mb-2">Admin Panel</h1>
            <p className="text-gray-600 text-base">Manage brands, channels, campaigns, and metric mappings</p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-white border border-gray-200">
              <TabsTrigger value="brands" data-testid="tab-brands">Brands</TabsTrigger>
              <TabsTrigger value="channels" data-testid="tab-channels">Channels</TabsTrigger>
              <TabsTrigger value="subchannels" data-testid="tab-subchannels">Sub-Channels</TabsTrigger>
              <TabsTrigger value="campaigns" data-testid="tab-campaigns">Campaigns</TabsTrigger>
              <TabsTrigger value="mappings" data-testid="tab-mappings">Metric Mappings</TabsTrigger>
              <TabsTrigger value="reports" data-testid="tab-reports">Report Types</TabsTrigger>
            </TabsList>

            {/* Brands Tab */}
            <TabsContent value="brands">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-[#0F0F0F] mb-4">Create Brand</h3>
                  <form onSubmit={createBrand} className="space-y-4">
                    <div>
                      <Label htmlFor="brand-name">Brand Name</Label>
                      <Input
                        id="brand-name"
                        value={brandName}
                        onChange={(e) => setBrandName(e.target.value)}
                        placeholder="e.g., My Brand"
                        className="bg-white border-gray-300"
                        data-testid="brand-name-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#32B4AC] hover:bg-[#2a9d95]" data-testid="create-brand-btn">
                      <Plus className="w-4 h-4 mr-2" /> Create Brand
                    </Button>
                  </form>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-[#0F0F0F] mb-4">Existing Brands</h3>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {brands.map(brand => (
                      <div key={brand.brand_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <span className="text-[#0F0F0F]">{brand.name}</span>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteBrand(brand.brand_id)}
                          data-testid={`delete-brand-${brand.brand_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Channels Tab */}
            <TabsContent value="channels">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-[#0F0F0F] mb-4">Create Channel</h3>
                  <form onSubmit={createChannel} className="space-y-4">
                    <div>
                      <Label htmlFor="channel-brand">Brand</Label>
                      <Select value={channelData.brand_id} onValueChange={(val) => setChannelData({...channelData, brand_id: val})}>
                        <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="channel-brand-select">
                          <SelectValue placeholder="Select brand" />
                        </SelectTrigger>
                        <SelectContent>
                          {brands.map(brand => (
                            <SelectItem key={brand.brand_id} value={brand.brand_id}>{brand.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="channel-name">Channel Name</Label>
                      <Input
                        id="channel-name"
                        value={channelData.name}
                        onChange={(e) => setChannelData({...channelData, name: e.target.value})}
                        placeholder="e.g., D2C, Amazon, Flipkart"
                        className="bg-gray-50 border-gray-300"
                        data-testid="channel-name-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#32B4AC] hover:bg-[#2a9d95]" data-testid="create-channel-btn">
                      <Plus className="w-4 h-4 mr-2" /> Create Channel
                    </Button>
                  </form>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-[#0F0F0F] mb-4">Existing Channels</h3>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {channels.map(channel => {
                      const brand = brands.find(b => b.brand_id === channel.brand_id);
                      return (
                        <div key={channel.channel_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div>
                            <div className="text-[#0F0F0F] font-medium">{channel.name}</div>
                            <div className="text-xs text-gray-600/60">{brand?.name}</div>
                          </div>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteChannel(channel.channel_id)}
                            data-testid={`delete-channel-${channel.channel_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* SubChannels Tab */}
            <TabsContent value="subchannels">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-[#0F0F0F] mb-4">Create Sub-Channel</h3>
                  <form onSubmit={createSubchannel} className="space-y-4">
                    <div>
                      <Label htmlFor="subchannel-channel">Channel</Label>
                      <Select value={subchannelData.channel_id} onValueChange={(val) => setSubchannelData({...subchannelData, channel_id: val})}>
                        <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="subchannel-channel-select">
                          <SelectValue placeholder="Select channel" />
                        </SelectTrigger>
                        <SelectContent>
                          {channels.map(channel => (
                            <SelectItem key={channel.channel_id} value={channel.channel_id}>{channel.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="subchannel-name">Sub-Channel Name</Label>
                      <Input
                        id="subchannel-name"
                        value={subchannelData.name}
                        onChange={(e) => setSubchannelData({...subchannelData, name: e.target.value})}
                        placeholder="e.g., Google Ads, Meta Ads"
                        className="bg-gray-50 border-gray-300"
                        data-testid="subchannel-name-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#32B4AC] hover:bg-[#2a9d95]" data-testid="create-subchannel-btn">
                      <Plus className="w-4 h-4 mr-2" /> Create Sub-Channel
                    </Button>
                  </form>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-[#0F0F0F] mb-4">Existing Sub-Channels</h3>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {subchannels.map(subchannel => {
                      const channel = channels.find(c => c.channel_id === subchannel.channel_id);
                      return (
                        <div key={subchannel.subchannel_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div>
                            <div className="text-[#0F0F0F] font-medium">{subchannel.name}</div>
                            <div className="text-xs text-gray-600/60">{channel?.name}</div>
                          </div>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteSubchannel(subchannel.subchannel_id)}
                            data-testid={`delete-subchannel-${subchannel.subchannel_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Campaigns Tab */}
            <TabsContent value="campaigns">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-[#0F0F0F] mb-4">Create Campaign</h3>
                  <form onSubmit={createCampaign} className="space-y-4">
                    <div>
                      <Label htmlFor="campaign-subchannel">Sub-Channel</Label>
                      <Select value={campaignData.subchannel_id} onValueChange={(val) => setCampaignData({...campaignData, subchannel_id: val})}>
                        <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="campaign-subchannel-select">
                          <SelectValue placeholder="Select sub-channel" />
                        </SelectTrigger>
                        <SelectContent>
                          {subchannels.map(subchannel => (
                            <SelectItem key={subchannel.subchannel_id} value={subchannel.subchannel_id}>{subchannel.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="campaign-name">Campaign Name</Label>
                      <Input
                        id="campaign-name"
                        value={campaignData.name}
                        onChange={(e) => setCampaignData({...campaignData, name: e.target.value})}
                        placeholder="e.g., AW_Summer_Sale"
                        className="bg-gray-50 border-gray-300"
                        data-testid="campaign-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="campaign-funnel">Funnel Stage (optional - auto-detected)</Label>
                      <Select value={campaignData.funnel_stage} onValueChange={(val) => setCampaignData({...campaignData, funnel_stage: val})}>
                        <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="campaign-funnel-select">
                          <SelectValue placeholder="Auto-detect" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="awareness">Awareness</SelectItem>
                          <SelectItem value="consideration">Consideration</SelectItem>
                          <SelectItem value="conversion">Conversion</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="w-full bg-[#32B4AC] hover:bg-[#2a9d95]" data-testid="create-campaign-btn">
                      <Plus className="w-4 h-4 mr-2" /> Create Campaign
                    </Button>
                  </form>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-[#0F0F0F] mb-4">Existing Campaigns</h3>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {campaigns.map(campaign => {
                      const subchannel = subchannels.find(s => s.subchannel_id === campaign.subchannel_id);
                      return (
                        <div key={campaign.campaign_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div>
                            <div className="text-[#0F0F0F] font-medium">{campaign.name}</div>
                            <div className="text-xs text-gray-600/60">
                              {subchannel?.name} • {campaign.funnel_stage} {campaign.auto_detected && '(auto)'}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteCampaign(campaign.campaign_id)}
                            data-testid={`delete-campaign-${campaign.campaign_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Metric Mappings Tab */}
            <TabsContent value="mappings">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-[#0F0F0F] mb-4">Create Metric Mapping</h3>
                  <form onSubmit={createMapping} className="space-y-4">
                    <div>
                      <Label htmlFor="mapping-source">Source Column Name</Label>
                      <Input
                        id="mapping-source"
                        value={mappingData.source_name}
                        onChange={(e) => setMappingData({...mappingData, source_name: e.target.value})}
                        placeholder="e.g., Total Clicks, Click Count"
                        className="bg-gray-50 border-gray-300"
                        data-testid="mapping-source-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mapping-standard">Standard Metric</Label>
                      <Select value={mappingData.standard_metric} onValueChange={(val) => setMappingData({...mappingData, standard_metric: val})}>
                        <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="mapping-standard-select">
                          <SelectValue placeholder="Select standard metric" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="impressions">Impressions</SelectItem>
                          <SelectItem value="clicks">Clicks</SelectItem>
                          <SelectItem value="ctr">CTR</SelectItem>
                          <SelectItem value="cpc">CPC</SelectItem>
                          <SelectItem value="spend">Spend</SelectItem>
                          <SelectItem value="revenue">Revenue</SelectItem>
                          <SelectItem value="roas">ROAS</SelectItem>
                          <SelectItem value="conversions">Conversions</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="mapping-type">Source Type</Label>
                      <Select value={mappingData.source_type} onValueChange={(val) => setMappingData({...mappingData, source_type: val})}>
                        <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="mapping-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="google_sheets">Google Sheets</SelectItem>
                          <SelectItem value="google_ads">Google Ads API</SelectItem>
                          <SelectItem value="meta_ads">Meta Ads API</SelectItem>
                          <SelectItem value="flipkart_ads">Flipkart Ads API</SelectItem>
                          <SelectItem value="amazon_ads">Amazon Ads API</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="w-full bg-[#32B4AC] hover:bg-[#2a9d95]" data-testid="create-mapping-btn">
                      <Plus className="w-4 h-4 mr-2" /> Create Mapping
                    </Button>
                  </form>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-[#0F0F0F] mb-4">Existing Mappings</h3>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {metricMappings.map(mapping => (
                      <div key={mapping.mapping_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div>
                          <div className="text-[#0F0F0F] font-medium">{mapping.source_name} → {mapping.standard_metric}</div>
                          <div className="text-xs text-gray-600/60">{mapping.source_type}</div>
                        </div>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteMapping(mapping.mapping_id)}
                          data-testid={`delete-mapping-${mapping.mapping_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
